import React, { useEffect, useRef } from 'react';
import { Assessment as AssessmentIcon } from '@material-ui/icons';
import Card from '../../../common/card/Card';
import CardContent from '../../../common/card/CardContent';
import CardTitle from '../../../common/card/CardTitle';
import InlineForm from '../../../common/inline/InlineForm';
import InlineFormik from '../../../common/inline/InlineFormik';
import InlineSelect from '../../../common/inline/InlineSelect';
import { useUpdateChangeOrder } from '../ChangeOrder.hook';
import {
  BaseChangeOrderCardProps,
  ChangeOrder
} from '../ChangeOrder.interface';
import { useUpdateTask } from '../../tasks/Task.hooks';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import InlineDatePicker from '../../../common/inline/InlineDatePicker';
import InlineHtml from '../../../common/inline/InlineHtml';

export interface ChangeOrderITAssessmentCardProps
  extends BaseChangeOrderCardProps {
  changeOrder: ChangeOrder;
  setSuccessMessage: (message: string) => void;
  setErrorMessage: (message: string) => void;
}

const ITAssessmentCardFields = [
  {
    name: 'assetInventoryNeedUpdated',
    label: 'Does the Asset Inventory need to be updated?'
  },
  {
    name: 'accessMatrixNeedUpdated',
    label: 'Does the Access Matrix need to be updated?'
  },
  {
    name: 'requireRiskAssessment',
    label: 'Does this change require a risk assessment?'
  },
  {
    name: 'requireAssetDowntime',
    label: 'Does this change require any asset downtime to perform?'
  }
];

export const ChangeOrderITAssessmentCard: React.FC<
  ChangeOrderITAssessmentCardProps
> = props => {
  const { changeOrder, onChange, setSuccessMessage, setErrorMessage } = props;
  const onChangeRef = useRef(onChange);
  const {
    updatedTask,
    updateError: updateTaskError,
    updateTask
  } = useUpdateTask(changeOrder.task.id || 0);
  const { updatedChangeOrder, isLoading, error, updateChangeOrder } =
    useUpdateChangeOrder(changeOrder.id);
  const { isFieldDisabled } = useTaskEntityFields(changeOrder.task);

  const initialValues: Partial<ChangeOrder> = {
    assetInventoryNeedUpdated: changeOrder.assetInventoryNeedUpdated || '',
    accessMatrixNeedUpdated: changeOrder.accessMatrixNeedUpdated || '',
    requireRiskAssessment: changeOrder.requireRiskAssessment || '',
    requireAssetDowntime: changeOrder.requireAssetDowntime,
    itDueDate: changeOrder.itDueDate,
    itCompletedDate: changeOrder.itCompletedDate,
    itComments: changeOrder.itComments || ''
  };

  useEffect(() => {
    if (updatedChangeOrder?.id) {
      setSuccessMessage('Change Order successfully updated');
      onChangeRef.current?.(updatedChangeOrder);
    }
  }, [setSuccessMessage, updatedChangeOrder]);

  useEffect(() => {
    if (updatedTask) {
      changeOrder.task = Object.assign(changeOrder.task || {}, updatedTask);
      onChange?.(changeOrder);
    }
  }, [updatedTask]);

  useEffect(() => {
    if (Boolean(error || updateTaskError))
      setErrorMessage('There was an error saving');
  }, [setErrorMessage, error, updateTaskError]);

  return (
    <InlineFormik
      initialValues={initialValues}
      strict={false}
      isSubmitting={isLoading}
      error={error}
      onSubmit={(updatedValue: Partial<ChangeOrder>) => {
        const dto = { ...updatedValue };
        updateChangeOrder(dto);
      }}
    >
      <Card>
        <CardTitle>
          <AssessmentIcon />
          IT Assessment
        </CardTitle>
        <CardContent padding='large'>
          <InlineForm>
            {ITAssessmentCardFields.map((field, key) => (
              <InlineSelect
                key={key}
                name={field.name}
                label={field.label}
                disabled={isFieldDisabled(field.name)}
                options={[
                  { value: 'yes', label: 'Yes' },
                  { value: 'no', label: 'No' },
                  { value: 'n/a', label: 'N/A' }
                ]}
              />
            ))}
            <InlineDatePicker
              grid={{ lg: 6, md: 6 }}
              clearable
              name='itDueDate'
              label='Due Date'
              disabled={isFieldDisabled('itDueDate')}
              placeholder='Enter Due Date'
              autoCloseOnSelect
            />
            <InlineDatePicker
              grid={{ lg: 6, md: 6 }}
              clearable
              name='itCompletedDate'
              label='Completed Date'
              disabled={isFieldDisabled('itCompletedDate')}
              placeholder='Enter Completed Date'
              autoCloseOnSelect
            />
            <InlineHtml
              name='itComments'
              label='Comments'
              disabled={isFieldDisabled('itComments')}
              helperText='Provide any additional information here'
              attachments={changeOrder.task.attachments || []}
              onChangeAttachments={attachments => {
                updateTask({ attachments });
                if (changeOrder.task) {
                  changeOrder.task.attachments = attachments;
                }
                onChange?.(changeOrder);
              }}
            />
          </InlineForm>
        </CardContent>
      </Card>
    </InlineFormik>
  );
};
